// @ts-strict-ignore
import { OrgComponentProps, allCases } from '../../typescriptHelpers';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Table, { ColumnsType } from 'antd/lib/table';
import Popover from 'antd/lib/popover';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectOrg, selectOrgUsers, selectUserAppDataByAppIdMap } from '../../selectors/selectors';
import { antGrey, getAvatarColorForName } from '../styles/globalStyles';
import { SettingOutlined, TeamOutlined, PlusOutlined, FileProtectOutlined } from '@ant-design/icons';
import { UserGroup, DefaultUserGroupId, SubscriptionFeatureType } from '../../types';
import RemoveGroupButton from './RemoveGroupButton';
import Button from 'antd/lib/button';
import AddEditUserGroupModal from './AddEditUserGroupModal';
import { selectSortedGroups } from '../../selectors/memoizedSelectors';
import Avatar from 'antd/lib/avatar';
import {
    displayTextForAppPlatform,
    displayUserNameShort,
    getUserActionDisplayName,
} from '../../helpers/displayHelpers';
import Tooltip from 'antd/lib/tooltip';
import Search from 'antd/lib/input/Search';
import Divider from 'antd/lib/divider';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import * as Log from '@/utils/logger';
import { compact } from 'lodash';

const GroupsTable: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const orgGroups = useSelector((state: RootState) => selectSortedGroups(state, props.orgId));
    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, props.orgId));
    const [selectedGroup, setSelectedGroup] = React.useState<UserGroup | null>(null);
    const [popoversVisibility, setPopoversVisibility] = React.useState({});
    const [showAddEditGroupModal, setShowAddEditGroupModal] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const [filteredGroups, setFilteredGroups] = React.useState<UserGroup[]>(orgGroups);
    const defaultGroupsIds = allCases(DefaultUserGroupId);
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const appDataByAppId = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, props.orgId));

    React.useEffect(() => {
        if (searchValue === '') {
            setFilteredGroups(orgGroups);
        } else {
            const filteredGroups = orgGroups.filter((group) =>
                group.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
            );

            setFilteredGroups(filteredGroups);
        }
    }, [searchValue, orgGroups]);

    const showAddGroupModal = () => {
        setShowAddEditGroupModal(true);
        setSelectedGroup(null);
    };

    const hideAddEditGroupModal = () => {
        setShowAddEditGroupModal(false);
        setSelectedGroup(null);
    };

    const showEditGroupModal = (group: UserGroup) => {
        handlePopoversVisibility();
        setShowAddEditGroupModal(true);
        setSelectedGroup(group);
    };

    const renderTitle = (title: string) => {
        return <Text strong>{title}</Text>;
    };

    //hide popovers after user actions
    const handlePopoversVisibility = () => {
        setPopoversVisibility({});
    };

    const actionContents = () => {
        return (
            <div style={{ width: 168 }}>
                <Button
                    type="link"
                    icon={<TeamOutlined style={{ color: antGrey.grey7 }} />}
                    onClick={() => showEditGroupModal(selectedGroup)}
                    style={{ color: antGrey.grey10, display: 'flex', alignItems: 'center' }}
                >
                    Edit group
                </Button>
                {selectedGroup && !defaultGroupsIds.includes(selectedGroup.id as DefaultUserGroupId) && (
                    <RemoveGroupButton
                        selectedGroup={selectedGroup}
                        orgId={props.orgId}
                        onGroupDeleted={handlePopoversVisibility}
                    />
                )}
            </div>
        );
    };

    const renderPermissionsTooltip = (group: UserGroup) => {
        const permissions = group.actions ?? [];
        return (
            <Tooltip
                overlayStyle={{ maxWidth: 450 }}
                title={
                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                        <Text strong style={{ color: 'inherit' }}>
                            Permissions
                        </Text>
                        {permissions.length > 0 ?
                            <ul style={{ paddingLeft: 16, margin: '8px 0' }}>
                                {permissions.map((action) => (
                                    <li key={action}>{getUserActionDisplayName(action)}</li>
                                ))}
                            </ul>
                        :   <div style={{ marginTop: 4 }}>No permissions set</div>}
                    </div>
                }
                placement="left"
            >
                <FileProtectOutlined style={{ fontSize: '17px', color: antGrey.grey8 }} />
            </Tooltip>
        );
    };

    const renderActions = (group: UserGroup, index: number) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 20 }}>
                {renderPermissionsTooltip(group)}
                <Popover
                    overlayClassName="org-settings-popover"
                    overlayStyle={{ paddingBottom: '0px' }}
                    trigger="click"
                    open={popoversVisibility[index] ?? false}
                    onOpenChange={(open) => {
                        const updatedPopoverVisibility = popoversVisibility;
                        updatedPopoverVisibility[index] = open;
                        setPopoversVisibility(updatedPopoverVisibility);
                        if (open || showAddEditGroupModal) {
                            setSelectedGroup(group);
                        } else {
                            setSelectedGroup(null);
                        }
                    }}
                    placement="topRight"
                    content={actionContents}
                    getPopupContainer={(trigger) => trigger.parentElement}
                    destroyTooltipOnHide={true}
                >
                    <SettingOutlined style={{ fontSize: '17px', paddingInlineEnd: '30px', color: antGrey.grey8 }} />
                </Popover>
            </div>
        );
    };

    const columns: ColumnsType = [
        {
            title: renderTitle('Group Name'),
            render: (value: UserGroup) => {
                if (defaultGroupsIds.includes(value.id as DefaultUserGroupId)) {
                    return (
                        <>
                            <Text key={value.id}>{value.name}</Text>
                            <Text type="secondary">{' (default)'}</Text>
                        </>
                    );
                } else {
                    return <Text key={value.id}>{value.name}</Text>;
                }
            },
            width: 450,
        },
        {
            title: renderTitle('Group Members'),
            render: (value: UserGroup) => {
                const members = value.members;

                if (members && members.length > 0) {
                    const membersOrgUsers = compact(
                        members.map((memberId) => {
                            const orgUser = orgUsers.find((orgUser) => orgUser.id === memberId);

                            if (!orgUser) {
                                Log.error(new Error(`User found in group ${value.name} has no org user: ${memberId}`));
                            }

                            return orgUser;
                        }),
                    );
                    const avatarMembers = membersOrgUsers.slice(0, 5);

                    const extraMembersCount = members.length - avatarMembers.length;

                    return (
                        <Tooltip
                            title={
                                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {membersOrgUsers.map(
                                        (member, index) =>
                                            member && <div key={index}>{displayUserNameShort(member)}</div>,
                                    )}
                                </div>
                            }
                            placement="topLeft"
                        >
                            <div>
                                <Avatar.Group>
                                    {avatarMembers.map((member) => {
                                        const memberDisplayName = displayUserNameShort(member);

                                        return (
                                            <Avatar
                                                key={member.id}
                                                src={member.commsAvatars?.image32}
                                                style={{
                                                    backgroundColor: getAvatarColorForName(memberDisplayName),
                                                }}
                                            >
                                                {memberDisplayName.charAt(0).toUpperCase()}
                                            </Avatar>
                                        );
                                    })}
                                    {extraMembersCount > 0 && (
                                        <Avatar key={'extraMembers'}>{`+${extraMembersCount}`}</Avatar>
                                    )}
                                </Avatar.Group>
                            </div>
                        </Tooltip>
                    );
                } else {
                    return <></>;
                }
            },
            width: 450,
        },
        {
            title: renderTitle('App membership'),
            render: (group: UserGroup) => {
                const appIds = (group.appIds ?? []).filter((appId) => !!appDataByAppId[appId]);
                if (appIds.length === 0) {
                    return <Text type="secondary">(none assigned)</Text>;
                }
                const appsToDisplay = appIds.slice(0, 3).map((appId) => appDataByAppId[appId]);
                const remainingApps = appIds.slice(3).map((appId) => appDataByAppId[appId]);
                const remainingAppsCountText = remainingApps.length === 1 ? 'other' : 'others';
                return (
                    <>
                        {appsToDisplay.map((appData, index) => (
                            <span key={appData.appId}>
                                {appData.appName}{' '}
                                <Text type="secondary">
                                    ({displayTextForAppPlatform(appData.platform, appData.appStoreType)})
                                </Text>
                                {index !== appsToDisplay.length - 1 && ', '}
                            </span>
                        ))}
                        {remainingApps.length > 0 && (
                            <Tooltip
                                title={
                                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                        {remainingApps.map((appData) => (
                                            <div key={appData.appId}>
                                                {appData.appName} (
                                                {displayTextForAppPlatform(appData.platform, appData.appStoreType)})
                                            </div>
                                        ))}
                                    </div>
                                }
                                placement="topLeft"
                            >
                                <Text type="secondary">
                                    {' '}
                                    + {remainingApps.length} {remainingAppsCountText}
                                </Text>
                            </Tooltip>
                        )}
                    </>
                );
            },
        },
        {
            title: '',
            align: 'right',
            render: (group: UserGroup, _, index) => {
                return renderActions(group, index);
            },
        },
    ];

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                }}
            >
                <Text
                    style={{
                        fontSize: 20,
                        color: antGrey.grey7,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {'Groups'}
                </Text>
                <div
                    style={{
                        flex: 1,
                        height: '100%',
                    }}
                >
                    <Divider
                        type="horizontal"
                        style={{
                            width: '100%',
                            margin: 0,
                        }}
                    />
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: 15 }}>
                <Search
                    placeholder="Search groups"
                    allowClear
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onSearch={setSearchValue}
                    style={{ width: 400 }}
                />
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.CustomGroups} requiresTooltip>
                    {(isDisabled) => (
                        <Button
                            onClick={showAddGroupModal}
                            type="primary"
                            icon={<PlusOutlined />}
                            disabled={isDisabled}
                        >
                            Create group
                        </Button>
                    )}
                </PlanRestrictedFeatureWrapper>
            </div>
            <Table
                dataSource={filteredGroups}
                columns={columns}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
                style={{ marginTop: 24, marginBottom: 24 }}
            />
            <AddEditUserGroupModal
                open={showAddEditGroupModal}
                onCancel={hideAddEditGroupModal}
                onSuccess={hideAddEditGroupModal}
                orgGroups={orgGroups}
                orgUsers={orgUsers}
                appDataByAppId={appDataByAppId}
                currentGroup={selectedGroup}
                orgId={props.orgId}
                isDirectorySyncEnabled={org.ssoSamlConfig?.ssoDirectory?.isActive ?? false}
                isUsingAdHocGroups={org.orgSettings?.isUsingAdHocGroups ?? false}
                disableEditingApps={org.ssoSamlConfig?.isUsingDirectorySyncDefinedApps ?? false}
                usePermissionsOnGroupLevel={org.usePermissionsOnGroupLevel}
            />
        </>
    );
};

export default GroupsTable;
